/* @jsxImportSource @emotion/react */
/* eslint-disable formatjs/no-literal-string-in-jsx */
import React from 'react'
import { css } from '@emotion/react'
import classNames from 'classnames'
import { useIntl } from 'react-intl'

import { SliteLogo, SuperSourceIcon } from '@slite/components'
import { SuperSource } from '@slite/search-client'

import { Button } from '../../../../components/Button/Button'
import { TextLayout } from '../../../../components/TextLayout'
import { useRoutes } from '../../../../routes'
import { SMALL_BREAKPOINT } from '../../../../utils/breakpoints'
import {
  desktopOnlyCss,
  mobileOnlyCss,
  mobileOnlyFlexCss,
  useIsMobile,
} from '../../../../utils/responsive'
import { CARD_SHADOW } from '../../../../utils/shadows'
import { COMPACT_SOURCE_LOGOS, SOURCE_LOGOS } from './sources'
import {
  BOX_HEIGHT,
  baseGridCss,
  INSIGHT_CSS,
  insightAnchorLeftOfLogoCss,
  insightAnchorRightOfLogoCss,
  insightArrowOnLeftCss,
  insightArrowOnRightCss,
  insightCardCss,
  ORBIT,
  scanLineCss,
  scanTrailCss,
} from './styles'

const SOURCE_LOGO_SIZE = 70
const SOURCE_LOGO_RADIUS = 18
const SOURCE_LOGO_ICON_SIZE = 34
const MOBILE_SOURCE_LOGO_SIZE = 44
const MOBILE_SOURCE_LOGO_RADIUS = 12
const MOBILE_SOURCE_LOGO_ICON_SIZE = 22
const MOBILE_BOX_HEIGHT = 400
// Compact embeds drop the in-box text, use the evenly-distributed half source
// set, and a shorter box that sits beside a page's own heading without towering
// over its neighbor.
const COMPACT_BOX_HEIGHT = 380

export function SliteAgentHero({
  title,
  description,
  compactMode = false,
}: {
  title?: string
  description?: string
  compactMode?: boolean
} = {}) {
  const boxHeight = compactMode ? COMPACT_BOX_HEIGHT : BOX_HEIGHT
  const intl = useIntl()
  const routes = useRoutes()
  const isMobile = useIsMobile()
  const logoSize = isMobile ? MOBILE_SOURCE_LOGO_SIZE : SOURCE_LOGO_SIZE
  const logoRadius = isMobile ? MOBILE_SOURCE_LOGO_RADIUS : SOURCE_LOGO_RADIUS
  const logoIconSize = isMobile
    ? MOBILE_SOURCE_LOGO_ICON_SIZE
    : SOURCE_LOGO_ICON_SIZE

  const content = {
    title:
      title ??
      intl.formatMessage({
        id: 'home.sliteAgent.title',
        defaultMessage: 'Slite rewrites outdated documentation on autopilot',
      }),
    description:
      description ??
      intl.formatMessage({
        id: 'home.sliteAgent.description',
        defaultMessage:
          'Slite Agent watches Slack, Linear, GitHub and your codebase, spots outdated facts in your documentation, and drafts the update for you to approve in one click.',
      }),
    button: (
      <Button href={routes.agent()} variant="secondary" withChevron>
        {intl.formatMessage({
          id: 'home.sliteAgent.cta',
          defaultMessage: 'See Slite Agent',
        })}
      </Button>
    ),
    illustration: (
      <div
        className="background-shade-00 flexbox flexbox-centered"
        style={{
          width: 80,
          height: 80,
          borderRadius: 22,
          zIndex: 1,
        }}
      >
        <svg
          className="moon-dust-lighter"
          width="48"
          height="48"
          viewBox={ORBIT.viewBox}
          aria-hidden="true"
          focusable="false"
          style={{ display: 'block', overflow: 'visible' }}
        >
          <path
            d={ORBIT.ribbonPath}
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeWidth="2.6"
          >
            <animate
              attributeName="d"
              dur={ORBIT.loopDuration}
              repeatCount="indefinite"
              calcMode="linear"
              keyTimes={ORBIT.keyTimes}
              values={ORBIT.ribbonPathValues}
            />
          </path>
          <g>
            <circle
              transform={ORBIT.leftEyeTransform}
              r="1.7"
              fill="currentColor"
            >
              <animateTransform
                attributeName="transform"
                type="translate"
                dur={ORBIT.loopDuration}
                repeatCount="indefinite"
                calcMode="linear"
                keyTimes={ORBIT.keyTimes}
                values={ORBIT.leftEyeTransformValues}
              />
            </circle>
            <circle
              transform={ORBIT.rightEyeTransform}
              r="1.7"
              fill="currentColor"
            >
              <animateTransform
                attributeName="transform"
                type="translate"
                dur={ORBIT.loopDuration}
                repeatCount="indefinite"
                calcMode="linear"
                keyTimes={ORBIT.keyTimes}
                values={ORBIT.rightEyeTransformValues}
              />
            </circle>
          </g>
        </svg>
      </div>
    ),
  }

  return (
    <>
      <div
        css={css`
          height: ${boxHeight}px;
          @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
            height: ${Math.min(boxHeight, MOBILE_BOX_HEIGHT)}px;
          }
        `}
        style={{
          position: 'relative',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'hidden',
        }}
      >
        <div css={baseGridCss} />
        <div css={scanTrailCss} />
        <div css={scanLineCss} />
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          {(compactMode ? COMPACT_SOURCE_LOGOS : SOURCE_LOGOS).map(
            ({ source, left, top, insight }) => {
              // Logos on the left half get tooltips to their right (and the
              // tooltip arrow on its left edge), and vice versa.
              const isLogoOnLeftSide = parseFloat(left) < 50
              const anchorCss = isLogoOnLeftSide
                ? insightAnchorRightOfLogoCss
                : insightAnchorLeftOfLogoCss
              const arrowCss = isLogoOnLeftSide
                ? insightArrowOnLeftCss
                : insightArrowOnRightCss
              return (
                <div
                  key={source}
                  className="flexbox flexbox-centered"
                  style={{
                    position: 'absolute',
                    left,
                    top,
                    transform: 'translate(-50%, -50%)',
                    width: logoSize,
                    height: logoSize,
                    borderRadius: logoRadius,
                    background: '#fff',
                    boxShadow: CARD_SHADOW,
                    // Lift the 4 logos that have insights above their plain
                    // neighbors so the highlight ring + tooltip aren't covered.
                    zIndex: insight ? 2 : 1,
                  }}
                >
                  {source === SuperSource.SLITE ? (
                    <SliteLogo
                      variant="symbol-no-bg"
                      style={{
                        width: logoIconSize,
                        height: logoIconSize,
                      }}
                    />
                  ) : (
                    <SuperSourceIcon sourceType={source} size={logoIconSize} />
                  )}
                  {insight && (
                    <>
                      <div
                        css={INSIGHT_CSS.get(source)?.border}
                        style={{
                          position: 'absolute',
                          inset: 0,
                          borderRadius: logoRadius,
                          border: '2px solid #000',
                          pointerEvents: 'none',
                        }}
                      />
                      <div css={anchorCss}>
                        <div
                          css={[
                            insightCardCss,
                            arrowCss,
                            INSIGHT_CSS.get(source)?.tooltip,
                          ]}
                          className={classNames(
                            'dark-mode background-shade-100 shade-00',
                            'radius-larger shadow-medium padding-vertical-8 padding-horizontal-12',
                            'font-size-6 font-weight-semibold'
                          )}
                        >
                          {insight}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )
            }
          )}
        </div>
        {/* Desktop: full TextLayout sits inside the box (illustration above
            title + description + CTA). */}
        {!compactMode && (
          <div css={desktopOnlyCss} style={{ position: 'relative', zIndex: 1 }}>
            <TextLayout
              type="centered"
              title={content.title}
              titleMaxWidth={500}
              description={content.description}
              descriptionMaxWidth={550}
              button={content.button}
              illustration={content.illustration}
            />
          </div>
        )}
        {/* Mobile (or compact mode on all sizes): only the orbit
            illustration anchors the center of the animation. */}
        <div
          css={compactMode ? undefined : mobileOnlyFlexCss}
          style={{
            position: 'absolute',
            inset: 0,
            display: compactMode ? 'flex' : undefined,
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
            pointerEvents: 'none',
          }}
        >
          {content.illustration}
        </div>
      </div>
      {/* Mobile: text block sits below the animation box. */}
      {!compactMode && (
        <div css={mobileOnlyCss} style={{ marginTop: 32 }}>
          <TextLayout
            type="centered"
            title={content.title}
            titleMaxWidth={500}
            description={content.description}
            descriptionMaxWidth={550}
            button={content.button}
          />
        </div>
      )}
    </>
  )
}
